import styled, { css } from "styled-components";
import { rem } from "../Theme/Theme.constants.js";
import { DefaultColor, ITheme } from "../Theme/Theme.types.js";
import { CheckboxProps, CheckboxStatus } from "./Checkbox.types.js";
import { CheckedIcon as InnerCheckedIcon } from "./components/CheckedIcon.js";
import { IndeterminateIcon as InnerIndeterminateIcon } from "./components/IndeterminateIcon.js";

const getSize = ({ size }: { size: NonNullable<CheckboxProps["size"]> }) => {
    const sizeMap: Record<NonNullable<CheckboxProps["size"]>, string> = {
        medium: rem(1),
        large: rem(1.875),
    };

    return css`
        width: ${sizeMap[size]};
        height: ${sizeMap[size]};
    `;
};

const getBorderRadius = ({ size, theme }: { size: NonNullable<CheckboxProps["size"]>; theme: ITheme }) => {
    const borderRadiusMap: Record<NonNullable<CheckboxProps["size"]>, string> = {
        medium: theme.radius.xxs,
        large: theme.radius.xxs,
    };

    return borderRadiusMap[size];
};

const getColor = ({ theme, color }: { theme: ITheme; color: NonNullable<CheckboxProps["color"]> }): DefaultColor => {
    const colorsMap: Record<NonNullable<CheckboxProps["color"]>, DefaultColor> = {
        primary: theme.colors.primary,
        success: theme.colors.success,
    };
    return colorsMap[color];
};

export const DOMCheckbox = styled.input<{ $size: NonNullable<CheckboxProps["size"]> }>`
    ${(props) => getSize({ size: props.$size })}
    margin: 0;
    opacity: 0;
    cursor: pointer;

    &:disabled {
        cursor: not-allowed;
    }
`;

export const FakeCheckbox = styled.span<{ status?: CheckboxStatus; size: NonNullable<CheckboxProps["size"]> }>`
    display: flex;
    justify-content: center;
    align-items: center;
    ${(props) => getSize(props)}
    border-radius: ${(props) => getBorderRadius(props)};
    color: white;
    background-color: transparent;
    border: 2px solid ${({ status, theme }) => (status === "error" ? theme.colors.error[100] : theme.grayscale[70])};
    transition: all 0.2s ease;

    & svg {
        transition: transform 0.1s ease-in 25ms;
        transform: scale(0);
    }
`;

export const CheckedIcon = styled(InnerCheckedIcon)``;
export const IndeterminateIcon = styled(InnerIndeterminateIcon)``;

export const CheckboxWrapper = styled.span<{ color: NonNullable<CheckboxProps["color"]> }>`
    display: grid;
    grid-template-areas: "checkbox";

    & > * {
        grid-area: checkbox;
    }

    & ${DOMCheckbox}:checked + ${FakeCheckbox}, ${DOMCheckbox}.indeterminate + ${FakeCheckbox} {
        background-color: ${(props) => getColor(props)[100]};
        border-color: ${(props) => getColor(props)[100]};
    }

    & ${FakeCheckbox} ${CheckedIcon}, ${FakeCheckbox} ${IndeterminateIcon} {
        display: none;
    }

    &
        ${DOMCheckbox}:checked:not(.indeterminate)
        + ${FakeCheckbox}
        ${CheckedIcon},
        ${DOMCheckbox}.indeterminate
        + ${FakeCheckbox}
        ${IndeterminateIcon} {
        display: block;
    }

    & ${DOMCheckbox}:checked + ${FakeCheckbox} ${CheckedIcon}, ${DOMCheckbox}.indeterminate + ${FakeCheckbox} ${IndeterminateIcon} {
        transform: scale(1);
    }

    & ${DOMCheckbox}:disabled + ${FakeCheckbox} {
        background-color: ${(props) => props.theme.grayscale[20]};
        border-color: ${(props) => props.theme.grayscale[70]};
        cursor: not-allowed;
    }

    & ${DOMCheckbox}:checked:disabled + ${FakeCheckbox}, ${DOMCheckbox}.indeterminate:disabled + ${FakeCheckbox} {
        background-color: ${(props) => props.theme.grayscale[50]};
        border-color: ${(props) => props.theme.grayscale[50]};
    }

    & ${DOMCheckbox}:focus-visible + ${FakeCheckbox} {
        box-shadow: ${(props) => (props.color === "primary" ? props.theme.shadows.focus.primary : props.theme.shadows.focus.neutral)};
    }
`;

export const Container = styled.label<{
    color: NonNullable<CheckboxProps["color"]>;
    disabled: CheckboxProps["disabled"];
    align: NonNullable<CheckboxProps["align"]>;
}>`
    display: flex;
    align-items: ${(props) => props.align};
    gap: ${({ theme }) => theme.spacing[8]};
    cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
    color: ${(props) => (props.disabled ? props.theme.grayscale[70] : "inherit")};

    &:hover ${DOMCheckbox}:not(:disabled):not(:focus-visible) + ${FakeCheckbox} {
        border-color: ${(props) => getColor(props)[110]};
    }

    &:hover
        :is(${DOMCheckbox}:checked:not(:disabled):not(:focus-visible), ${DOMCheckbox}.indeterminate:not(:disabled):not(:focus-visible))
        + ${FakeCheckbox} {
        background-color: ${(props) => getColor(props)[110]};
        border-color: ${(props) => getColor(props)[110]};
    }

    &:hover ${DOMCheckbox}.indeterminate:not(:disabled):not(:focus-visible) + ${FakeCheckbox} {
        background-color: ${(props) => getColor(props)[110]};
        border-color: ${(props) => getColor(props)[110]};
    }
`;

export const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${(props) => props.theme.spacing[8]};
`;
